import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import DailyJournal from "./pages/DailyJournal";
import MyPou from "./pages/MyPou";
import Community from "./pages/Community";
import HauoraAI from "./pages/HauoraAI";
import Profile from "./pages/Profile";
import AdminDashboard from "./pages/AdminDashboard";
import Classes from "./pages/Classes";
import Messages from "./pages/Messages";
import MobileLayout from "./components/MobileLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Tier constants
export const TIER_FREE = "free";
export const TIER_MEMBER = "member";
export const TIER_CHALLENGER = "challenger";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("tribe_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      console.error("Auth error:", err);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("tribe_token", res.data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    toast.success("Kia ora! Welcome back");
    return res.data;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem("tribe_token", res.data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    toast.success("Nau mai! Welcome to The TRIBE");
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("tribe_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  // Access helpers
  const isMemberOrAbove = () => {
    return user && (user.tier !== TIER_FREE || ["coach", "admin"].includes(user.role));
  };

  const isChallenger = () => {
    return user && (user.tier === TIER_CHALLENGER || ["coach", "admin"].includes(user.role));
  };

  const isCoachOrAdmin = () => {
    return user && ["coach", "admin"].includes(user.role);
  };

  return (
    <AuthContext.Provider value={{ 
      user, token, loading, 
      login, register, logout, updateUser,
      isMemberOrAbove, isChallenger, isCoachOrAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="w-8 h-8 border-2 border-[#0B3D2E]/20 border-t-[#0B3D2E] rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin/Coach Route
const AdminRoute = ({ children }) => {
  const { user, loading, isCoachOrAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="w-8 h-8 border-2 border-[#0B3D2E]/20 border-t-[#0B3D2E] rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isCoachOrAdmin()) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            
            {/* Protected routes with mobile layout */}
            <Route path="/" element={
              <ProtectedRoute>
                <MobileLayout>
                  <Dashboard />
                </MobileLayout>
              </ProtectedRoute>
            } />
            <Route path="/journal" element={
              <ProtectedRoute>
                <MobileLayout>
                  <DailyJournal />
                </MobileLayout>
              </ProtectedRoute>
            } />
            <Route path="/pou" element={
              <ProtectedRoute>
                <MobileLayout>
                  <MyPou />
                </MobileLayout>
              </ProtectedRoute>
            } />
            <Route path="/classes" element={
              <ProtectedRoute>
                <MobileLayout>
                  <Classes />
                </MobileLayout>
              </ProtectedRoute>
            } />
            <Route path="/community" element={
              <ProtectedRoute>
                <MobileLayout>
                  <Community />
                </MobileLayout>
              </ProtectedRoute>
            } />
            <Route path="/messages" element={
              <ProtectedRoute>
                <MobileLayout>
                  <Messages />
                </MobileLayout>
              </ProtectedRoute>
            } />
            <Route path="/ai" element={
              <ProtectedRoute>
                <MobileLayout>
                  <HauoraAI />
                </MobileLayout>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <MobileLayout>
                  <Profile />
                </MobileLayout>
              </ProtectedRoute>
            } />
            
            {/* Admin routes */}
            <Route path="/admin" element={
              <AdminRoute>
                <MobileLayout>
                  <AdminDashboard />
                </MobileLayout>
              </AdminRoute>
            } />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
